/**
 * Customer Sign-Offs API.
 *
 * An installer + client acknowledgement created when an installation appointment
 * is completed. Lifecycle: Pending -> Completed | Void. Signatures are stored as
 * PNG data URLs (see src/components/SignaturePad.tsx).
 */

import { API_BASE } from '../config/apiBase';
import type { CustomerSignoffFormData } from '../data/customerSignoffForm';

export type CustomerSignoffStatus = 'Pending' | 'Completed' | 'Void';

export interface CustomerSignoffRow {
  id: number;
  installation_appointment_id: number | null;
  client_id: number | null;
  /** The linked client's Job Code / Deployment ID, e.g. A-000101. */
  job_code: string | null;
  business_name: string;
  status: CustomerSignoffStatus;
  installer_name: string | null;
  installer_signature: string | null;
  installer_signed_at: string | null;
  client_name: string | null;
  client_signature: string | null;
  client_signed_at: string | null;
  void_reason: string | null;
  /** Full multi-section acceptance form (see src/data/customerSignoffForm.ts). */
  form_data: CustomerSignoffFormData;
  /** Set when the employee finished their part and handed off to the client. */
  employee_completed_at: string | null;
  /** True once a completed-sign-off PDF has been stored. */
  has_pdf: boolean;
  created_by_user_id: number | null;
  created_by_username: string | null;
  completed_at: string | null;
  voided_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface CustomerSignoffListResponse {
  pending: CustomerSignoffRow[];
  completed: CustomerSignoffRow[];
  void: CustomerSignoffRow[];
}

async function jsonOrThrow<T>(res: Response, fallback: string): Promise<T> {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || fallback);
  return data as T;
}

export async function fetchCustomerSignoffs(): Promise<CustomerSignoffListResponse> {
  const res = await fetch(`${API_BASE}/api/customer-signoffs`);
  return jsonOrThrow(res, 'Failed to fetch sign-offs');
}

export async function fetchCustomerSignoff(id: number): Promise<CustomerSignoffRow> {
  const res = await fetch(`${API_BASE}/api/customer-signoffs/${id}`);
  return jsonOrThrow(res, 'Failed to fetch sign-off');
}

export interface CreateCustomerSignoffPayload {
  installation_appointment_id?: number | null;
  client_id?: number | null;
  business_name: string;
  created_by_user_id?: number;
}

export async function createCustomerSignoff(payload: CreateCustomerSignoffPayload): Promise<CustomerSignoffRow> {
  const res = await fetch(`${API_BASE}/api/customer-signoffs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return jsonOrThrow(res, 'Failed to create sign-off');
}

export interface UpdateCustomerSignoffPayload {
  installer_name?: string | null;
  installer_signature?: string | null;
  client_name?: string | null;
  client_signature?: string | null;
  status?: CustomerSignoffStatus;
  void_reason?: string | null;
  /** Full/partial form contents to persist (replaces stored form_data). */
  form_data?: CustomerSignoffFormData;
  /** Mark (or unmark) the employee's part complete, unlocking client review. */
  employee_completed?: boolean;
  /** Base64 (or data URL) PDF of the completed form, stored + emailed on completion. */
  pdf_base64?: string;
}

export async function updateCustomerSignoff(id: number, payload: UpdateCustomerSignoffPayload): Promise<CustomerSignoffRow> {
  const res = await fetch(`${API_BASE}/api/customer-signoffs/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return jsonOrThrow(res, 'Failed to update sign-off');
}

/** URL of the stored completed-sign-off PDF (only valid once has_pdf is true). */
export function customerSignoffPdfUrl(id: number): string {
  return `${API_BASE}/api/customer-signoffs/${id}/pdf`;
}
